import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  advancedFilter,
  AscWageSummaryFilter,
  Conditions,
  FilterField,
  GET_ASC_WAGE_SUMMARY,
  GetAscWageSummaryQuery,
  GetAscWageSummaryQueryVariables,
  keywordFilter,
  useSticky,
} from '@neo.tax/shared';

import { useAscContext } from '../context';

export type AscWageSummary = NonNullable<GetAscWageSummaryQuery['getAscWageSummary']>;

type UseAscWageSummaryProps = {
  filterConditions?: Conditions<FilterField<AscWageSummaryFilter>>;
  searchKeyword?: string | null;
};

type UseAscWageSummaryResult =
  | {
      loading: true;
      ascWageSummary: null;
    }
  | {
      loading: false;
      ascWageSummary: AscWageSummary;
    };

export const useAscWageSummary = ({
  filterConditions,
  searchKeyword,
}: UseAscWageSummaryProps = {}): UseAscWageSummaryResult => {
  const { fiscalYearId, periodName } = useAscContext();

  const {
    data: requestData,
    error,
    loading,
  } = useQuery<GetAscWageSummaryQuery, GetAscWageSummaryQueryVariables>(GET_ASC_WAGE_SUMMARY, {
    variables: {
      fiscalYearId,
      periodName,
      filter: {
        and: [keywordFilter(['name'], searchKeyword), advancedFilter<AscWageSummaryFilter>(filterConditions)],
      },
    },
  });

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const ascWageSummary = stickyData?.getAscWageSummary ?? null;

  if (loading) {
    return {
      loading: true,
      ascWageSummary: null,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData || !ascWageSummary) {
    throw createError('MissingAscWageSummary', 'No data returned from getAscWageSummary query');
  }

  return {
    loading,
    ascWageSummary,
  };
};
